import { z } from "zod";
import {
  Axis,
  BarSeries,
  Box,
  ChartGrid,
  ChartTooltip,
  Text,
  WidgetEmpty,
  WidgetSkeleton,
  XYChart,
  compactNumber,
  useCategoricalColors,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
  truncateLabel,
} from "@salestrak/ui";
import { useStageMoves, type StageMoveSlice } from "./use-stage-moves";
import { WidgetChartTooltip } from "../shared/widget-chart-tooltip";
import { resolveStoredTitle } from "../shared/derived-titles";

export const stageMovesConfigSchema = z
  .looseObject({
    mode: z.enum(["byStage", "total"]).default("byStage"),
    limit: z.int().min(3).max(12).default(8),
    showGrid: z.boolean().default(true),
    showAxes: z.boolean().default(true),
  });

export type StageMovesConfig = z.infer<typeof stageMovesConfigSchema>;

/** Title mechanics: the per-mode derived titles are widget-generated and
 *  re-derive when the mode flips; renames stick. */
const MOVES_MODE_TITLE: Record<StageMovesConfig["mode"], string> = {
  byStage: "Moved into stage",
  total: "Total stage moves",
};
const GENERATED_MOVES_TITLES = new Set(Object.values(MOVES_MODE_TITLE));

const xAccessor = (d: StageMoveSlice) => d.count;
const yAccessor = (d: StageMoveSlice) => d.label;

function StageMovesRender({ h, config }: WidgetRenderContext<StageMovesConfig>) {
  const { total, slices, loading } = useStageMoves(config.mode, config.limit);
  const sliceColors = useCategoricalColors(slices);

  if (loading) {
    return <WidgetSkeleton variant="rows" />;
  }

  if (config.mode === "total") {
    return (
      <Box fill="flex" align="center" justify="center" paddingY="xs">
        <Box align="center" gap="2xs">
          <Text size={h >= 3 ? "4xl" : "2xl"} weight="bold" truncate={1}>
            {total.toLocaleString()}
          </Text>
          <Text size="sm" variant="muted" truncate={1}>
            stage moves in the window
          </Text>
        </Box>
      </Box>
    );
  }

  if (slices.length === 0) {
    return <WidgetEmpty icon="bar-chart-3" title="No stage moves in range" />;
  }

  const compact = h < 4;
  const showAxes = config.showAxes && !compact;
  const showGrid = config.showGrid && !compact;

  return (
    <Box direction="column" fill="flex" gap="xs">
      <XYChart<StageMoveSlice>
        xScale={{ type: "linear", nice: true }}
        yScale={{ type: "band", paddingInner: 0.3, paddingOuter: 0.2 }}
        margin={{ top: 8, right: 12, bottom: showAxes ? 28 : 8, left: showAxes ? 96 : 8 }}
        ariaLabel="Moves into stage"
      >
        {showGrid ? <ChartGrid columns rows={false} numTicks={4} /> : null}
        {showAxes ? <Axis orientation="bottom" numTicks={4} tickFormat={compactNumber} /> : null}
        {showAxes ? <Axis orientation="left" tickFormat={(v) => truncateLabel(String(v), 14)} /> : null}
        <BarSeries
          dataKey="moves"
          data={slices}
          xAccessor={xAccessor}
          yAccessor={yAccessor}
          colorAccessor={(d) => sliceColors.scale(d.label)}
        />
        <ChartTooltip
          snapTooltipToDatumY
          showHorizontalCrosshair
          renderTooltip={({ tooltipData }) => {
            const d = tooltipData?.nearestDatum?.datum as StageMoveSlice | undefined;
            if (!d) return null;
            return (
              <WidgetChartTooltip
                label={d.label}
                details={`${d.count} ${d.count === 1 ? "move" : "moves"} into stage`}
              />
            );
          }}
        />
      </XYChart>
    </Box>
  );
}

const stageMovesSettings: WidgetSettingsGroup[] = [
  {
    title: "Data",
    fields: [
      {
        key: "mode",
        kind: "select",
        label: "Mode",
        icon: "bar-chart-3",
        options: [
          { value: "byStage", label: "By stage" },
          { value: "total", label: "Total" },
        ],
      },
      { key: "limit", kind: "number", label: "Max stages", icon: "list", min: 3, max: 12, step: 1 },
    ],
  },
  {
    title: "Display",
    fields: [
      { key: "showGrid", kind: "toggle", label: "Gridlines", icon: "grid-dots" },
      { key: "showAxes", kind: "toggle", label: "Axes", icon: "ruler" },
    ],
  },
];

export const stageMovesDef: WidgetModule<StageMovesConfig> = {
  type: "stage-moves",
  title: "Moved into stage",
  dynamicTitle: (config) =>
    resolveStoredTitle(
      config.title,
      (title) => GENERATED_MOVES_TITLES.has(title),
      MOVES_MODE_TITLE[config.mode],
    ),
  icon: "bar-chart-3",
  span: { minW: 3, minH: 2, maxW: 12, maxH: 8, defaultW: 5, defaultH: 4 },
  configSchema: stageMovesConfigSchema,
  settings: stageMovesSettings,
  catalog: [
    {
      key: "stage-moves.byStage",
      label: "Moved into stage",
      description: "Counts stage-transition events into each stage during the window, not projects currently in that stage.",
      section: "Charts",
      // Label is in the generated family — bake nothing, let `dynamicTitle` track.
      autoTitle: false,
      defaultConfig: { mode: "byStage" },
      defaultW: 5,
      defaultH: 4,
    },
  ],
  render: StageMovesRender,
};
